/** Grid of spark rows: each item is a card (rounded, border) with name, description, change badge, and spark area chart. */
import { TrendBadge } from "@/components/ui/data-display/TrendBadge";
import { cn } from "@/lib/utils";
import type { AvailableChartColorsKeys } from "@/lib/utils/chartUtils";
import { SparkAreaChart } from "./SparkAreaChart";
import type { SparkChartRowItem, SparkChartRowProps } from "./SparkChart.types";

const ROW_CHART_COLORS: Record<
  "negative" | "neutral" | "positive",
  AvailableChartColorsKeys[]
> = {
  positive: ["green"],
  negative: ["rose"],
  neutral: ["gray"],
};

const SparkChartRowItemView = ({
  item,
  data,
  index,
}: {
  item: SparkChartRowItem;
  data: SparkChartRowProps["data"];
  index: string;
}) => {
  const colors =
    item.changeType === "positive"
      ? ROW_CHART_COLORS.positive
      : item.changeType === "negative"
        ? ROW_CHART_COLORS.negative
        : item.changeType === "neutral"
          ? ROW_CHART_COLORS.neutral
          : undefined;

  return (
    <div className="flex h-full w-fit min-w-0 items-start justify-between gap-4 overflow-visible rounded-lg border border-gray-200 bg-white p-4">
      <div className="min-w-0 flex-1 overflow-visible">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium leading-tight text-gray-900 wrap-break-word">
            {item.name}
          </span>
          {item.change != null &&
          item.change !== "" &&
          item.changeType != null ? (
            <TrendBadge
              size="xs"
              variant={item.changeType}
              value={item.change}
            />
          ) : null}
        </div>
        {item.description != null && item.description !== "" ? (
          <p className="mt-1 text-xxs leading-snug text-gray-500">
            {item.description}
          </p>
        ) : null}
      </div>
      <div className="shrink-0 overflow-hidden">
        <SparkAreaChart
          data={data}
          index={index}
          categories={[item.category]}
          colors={colors}
          fill="gradient"
          width={112}
          height={40}
        />
      </div>
    </div>
  );
};

export const SparkChartRow = ({
  items,
  data,
  index,
  className,
}: SparkChartRowProps) => (
  <>
    {/* Keep chart stroke/text classes in bundle (Tailwind may purge conditional/dynamic classes). */}
    <span className="hidden stroke-green-700" aria-hidden />
    <span className="hidden stroke-rose-500" aria-hidden />
    <span className="hidden text-green-700" aria-hidden />
    <span className="hidden text-rose-500" aria-hidden />
    <ul className={cn("flex flex-wrap gap-6", className)}>
      {items.map((item) => (
        <li
          key={`${item.name}-${item.category}`}
          className="h-full w-fit min-w-[200px] list-none shrink-0"
        >
          <SparkChartRowItemView item={item} data={data} index={index} />
        </li>
      ))}
    </ul>
  </>
);
